import type { TranslationOverride } from "@/features/internationalisation/types";
import { HubTheme } from "./types";

export const SSP_LABELS_STORAGE_KEY = "ssp-scenario-labels";

/**
 * Scenario labels named by SSP alone, with the NGFS alignment of the default labels. RCP 6.0 has no SSP in the
 * default labels and maps to SSP3-7.0. `name` and `gwl` stay as the theme sets them.
 */
const sspScenarioTerms: TranslationOverride = {
  terms: {
    rcp: "Shared Socioeconomic Pathway",
    rcpShort: "SSP",
    scenarios: {
      rcp26: {
        long: "XDI SSP1-2.6 aligns with NGFS Net Zero 2050",
        medium: "SSP1-2.6 (Net Zero 2050)",
        short: "SSP1-2.6",
      },
      rcp45: {
        long: "XDI SSP2-4.5 aligns with NGFS Delayed Transition",
        medium: "SSP2-4.5 (Delayed Transition)",
        short: "SSP2-4.5",
      },
      rcp60: {
        long: "XDI SSP3-7.0 aligns with NGFS Current Policies (median)",
        medium: "SSP3-7.0 (Current Policies Median Range)",
        short: "SSP3-7.0",
      },
      rcp85: {
        long: "XDI SSP5-8.5 aligns with NGFS Current Policies (high range)",
        medium: "SSP5-8.5 (Current Policies High Range)",
        short: "SSP5-8.5",
      },
    },
  },
};

const browserStorage = (): Storage | undefined => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
};

export const isSspLabelsToggleEnabled = (theme: HubTheme) => !!theme.meta.config.featureSet.sspScenarioLabels?.enabled;

export const readSspLabelsChoice = (storage = browserStorage()) => {
  try {
    return storage?.getItem(SSP_LABELS_STORAGE_KEY) === "on";
  } catch {
    return false;
  }
};

export const storeSspLabelsChoice = (on: boolean, storage = browserStorage()) => {
  try {
    if (on) {
      storage?.setItem(SSP_LABELS_STORAGE_KEY, "on");
    } else {
      storage?.removeItem(SSP_LABELS_STORAGE_KEY);
    }
  } catch {
    // Private browsing: the labels stay as the theme sets them.
  }
};

/** Returns the SSP label overrides when the theme allows them and this user has switched them on. */
export const getSspLabelOverrides = (theme: HubTheme, storage = browserStorage()) =>
  isSspLabelsToggleEnabled(theme) && readSspLabelsChoice(storage) ? sspScenarioTerms : undefined;
